import { useState, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import styles from './Navbar.module.css';

const navLinks = [
  { label: 'Home',         path: '/' },
  { label: 'About',        path: '/about' },
  { label: 'Projects',     path: '/portfolio' },
  { label: 'Blog',         path: '/blog' },
  { label: 'Mentorship',   path: '/mentorship' },
  { label: 'Testimonials', path: '/testimonials' },
];

const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="5"/>
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1111.21 3a7 7 0 109.79 9.79z"/>
  </svg>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const handleLogoClick = (e) => {
    closeMenu();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // If already on home, prevent re-render; otherwise navigate
    if (window.location.pathname !== '/') {
      navigate('/');
    }
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      <div className={styles.inner}>

        {/* ── Logo ── */}
        <Link to="/" className={styles.logo} onClick={handleLogoClick} aria-label="Labora home">
          <svg className={styles.logoIcon} viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path
              d="M11 4h10M13 4v9.5L6.5 22a5 5 0 004.3 7.5h10.4A5 5 0 0025.5 22L19 13.5V4"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            />
            <circle cx="12.5" cy="22.5" r="1.5" fill="currentColor" opacity="0.7" />
            <circle cx="18"   cy="24.5" r="1"   fill="currentColor" opacity="0.5" />
            <circle cx="15"   cy="20.5" r="1"   fill="currentColor" opacity="0.6" />
          </svg>
          <span className={styles.logoText}>Labora</span>
        </Link>

        {/* ── Desktop Nav Links ── */}
        <nav className={styles.nav} aria-label="Primary navigation">
          {navLinks.map(({ label, path }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) =>
                `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* ── Desktop Actions ── */}
        <div className={styles.actions}>
          <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
          <Link to="/signup" className={styles.ctaBtn}>
            Start Documenting
          </Link>
        </div>

        {/* ── Mobile Theme Toggle ── */}
        <button
          className={styles.mobileThemeToggle}
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>

        {/* ── Hamburger (mobile) ── */}
        <button
          className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* ── Mobile Menu Overlay ── */}
      <div
        className={`${styles.mobileOverlay} ${menuOpen ? styles.mobileOverlayOpen : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav className={styles.mobileNav} aria-label="Mobile navigation">
          {navLinks.map(({ label, path }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) =>
                `${styles.mobileNavLink} ${isActive ? styles.mobileNavLinkActive : ''}`
              }
              onClick={closeMenu}
            >
              {label}
            </NavLink>
          ))}
          <Link to="/signup" className={styles.mobileCta} onClick={closeMenu}>
            Start Documenting
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
